import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, FileText, Loader2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PayslipCard } from "@/components/payroll/PayslipCard";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { usePayrollPayslips } from "@/lib/api/payroll-payslips.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { PayrollPayslipStatusFilter } from "@/lib/api/payroll-payslips";

export const Route = createFileRoute("/companies/$companyId/nomina/payslips/")({
  component: PayslipsIndexPage,
});

function PayslipsIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PayslipsIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function PayslipsIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [statusFilter, setStatusFilter] = useState<PayrollPayslipStatusFilter>("all");
  const filters = useMemo(
    () => ({ status: statusFilter, pageSize: 50 }),
    [statusFilter],
  );
  const payslipsQ = usePayrollPayslips(companyId, filters);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="nomina" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/nomina"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("payroll.payslip.list.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("payroll.payslip.list.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("payroll.payslip.list.subtitle")}
          </p>
        </div>
        <Button asChild>
          <Link
            to="/companies/$companyId/nomina/payslips/new"
            params={{ companyId }}
          >
            <Plus className="h-4 w-4" />
            {t("payroll.payslip.list.new")}
          </Link>
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as PayrollPayslipStatusFilter)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(["all", "ready_to_emit", "draft", "queued", "processing", "transmitted", "failed", "replaced", "voided"] as const).map(
              (s) => (
                <SelectItem key={s} value={s}>
                  {t(`payroll.payslip.filter.${s}`)}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
      </div>

      {payslipsQ.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : payslipsQ.isError ? (
        <ErrorState
          title={t("payroll.payslip.list.error_title")}
          description={t("payroll.payslip.list.error_body")}
          onRetry={() => void payslipsQ.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : (payslipsQ.data?.payslips.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <FileText className="h-8 w-8 text-primary" />
            </div>
            <div className="max-w-sm space-y-1">
              <h2 className="text-lg font-semibold">{t("payroll.payslip.list.empty_title")}</h2>
              <p className="text-sm text-muted-foreground">{t("payroll.payslip.list.empty_body")}</p>
            </div>
            <Button asChild>
              <Link
                to="/companies/$companyId/nomina/payslips/new"
                params={{ companyId }}
              >
                <Plus className="h-4 w-4" />
                {t("payroll.payslip.list.new")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {payslipsQ.data!.payslips.map((p) => (
            <PayslipCard key={p.id} companyId={companyId} payslip={p} />
          ))}
        </div>
      )}
    </div>
  );
}
